import { useEffect, useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { CanvasInView, Reveal } from "./Reveal";

function Rig() {
  const { camera } = useThree();
  useEffect(() => {
    camera.lookAt(0, 0.4, 0);
  }, [camera]);
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    camera.position.x = 8.5 + Math.sin(t * 0.05) * 0.9;
    camera.position.y = 1.7 + Math.sin(t * 0.07) * 0.15;
    camera.lookAt(0, 0.4, 0);
  });
  return null;
}

function Ocean() {
  const geo = useMemo(() => new THREE.PlaneGeometry(70, 70, 90, 90), []);
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    const pos = geo.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      pos.setZ(i, Math.sin(x * 0.32 + t * 0.9) * 0.22 + Math.cos(y * 0.26 + t * 0.7) * 0.18);
    }
    pos.needsUpdate = true;
    geo.computeVertexNormals();
  });
  return (
    <mesh geometry={geo} rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.55, 0]}>
      <meshStandardMaterial color="#071523" metalness={0.55} roughness={0.35} />
    </mesh>
  );
}

function Tanker() {
  const g = useRef();
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    if (g.current) {
      g.current.position.y = Math.sin(t * 0.5) * 0.08;
      g.current.rotation.z = Math.sin(t * 0.4) * 0.015;
      g.current.rotation.x = Math.cos(t * 0.33) * 0.012;
    }
  });
  const hull = { color: "#0e2036", metalness: 0.6, roughness: 0.4 };
  const steel = { color: "#b8c4d4", metalness: 0.85, roughness: 0.28 };
  return (
    <group ref={g}>
      <mesh>
        <boxGeometry args={[7.2, 0.9, 1.5]} />
        <meshStandardMaterial {...hull} />
      </mesh>
      <mesh position={[0, -0.42, 0]}>
        <boxGeometry args={[7.24, 0.14, 1.52]} />
        <meshStandardMaterial color="#5e1f1f" metalness={0.5} roughness={0.5} />
      </mesh>
      <mesh position={[4.25, 0.02, 0]} rotation={[0, Math.PI / 4, -Math.PI / 2]} scale={[1, 1, 0.62]}>
        <coneGeometry args={[1.05, 1.7, 4]} />
        <meshStandardMaterial {...hull} />
      </mesh>
      <mesh position={[0, 0.5, 0]}>
        <boxGeometry args={[7.0, 0.12, 1.35]} />
        <meshStandardMaterial color="#152c47" metalness={0.6} roughness={0.45} />
      </mesh>
      {[-2.3, -1.15, 0, 1.15, 2.3].map((x) => (
        <mesh key={x} position={[x, 0.85, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.42, 0.42, 0.95, 24]} />
          <meshStandardMaterial {...steel} />
        </mesh>
      ))}
      <mesh position={[0, 1.12, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.06, 0.06, 5.6, 12]} />
        <meshStandardMaterial color="#D9A441" emissive="#D9A441" emissiveIntensity={0.25} metalness={0.7} roughness={0.3} />
      </mesh>
      <mesh position={[-3.25, 1.15, 0]}>
        <boxGeometry args={[0.95, 1.2, 1.15]} />
        <meshStandardMaterial color="#dfe6ee" metalness={0.35} roughness={0.5} />
      </mesh>
      <mesh position={[-3.25, 1.55, 0]}>
        <boxGeometry args={[0.97, 0.16, 1.17]} />
        <meshStandardMaterial color="#0e2036" emissive="#F0C05A" emissiveIntensity={0.35} />
      </mesh>
      <mesh position={[-3.25, 2.05, 0]}>
        <cylinderGeometry args={[0.16, 0.2, 0.6, 16]} />
        <meshStandardMaterial {...hull} />
      </mesh>
      <mesh position={[-3.25, 2.25, 0]}>
        <cylinderGeometry args={[0.17, 0.17, 0.12, 16]} />
        <meshStandardMaterial color="#D9A441" emissive="#D9A441" emissiveIntensity={0.5} />
      </mesh>
      <mesh position={[3.1, 1.15, 0]}>
        <cylinderGeometry args={[0.035, 0.035, 1.1, 8]} />
        <meshStandardMaterial {...steel} />
      </mesh>
      <mesh position={[3.1, 1.65, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.025, 0.025, 0.7, 8]} />
        <meshStandardMaterial {...steel} />
      </mesh>
    </group>
  );
}

function TankerScene() {
  return (
    <>
      <fog attach="fog" args={["#04070d", 9, 32]} />
      <ambientLight intensity={0.3} color="#3a5a7a" />
      <directionalLight position={[-7, 4, -5]} intensity={2.2} color="#F0C05A" />
      <pointLight position={[6, 3, 6]} intensity={18} color="#00E5FF" />
      <Rig />
      <Ocean />
      <Tanker />
    </>
  );
}

export default function TankerSection() {
  return (
    <section id="global-movement" data-testid="tanker-section" className="relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_100%,rgba(240,192,90,0.07),transparent_60%)] pointer-events-none" />
      <div className="relative">
        <CanvasInView
          className="h-[55vh] md:h-[70vh] w-full"
          camera={{ position: [8.5, 1.7, 7.5], fov: 38 }}
          dpr={[1, 1.75]}
        >
          <TankerScene />
        </CanvasInView>
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-ink to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-ink to-transparent pointer-events-none" />
        <div className="absolute bottom-10 inset-x-0">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal>
              <p className="font-outfit text-2xl md:text-4xl font-extralight text-white tracking-tight [text-shadow:0_2px_28px_rgba(3,5,8,0.95)]" data-testid="tanker-caption">
                Energy moves. <span className="text-gold">We coordinate the journey.</span>
              </p>
              <p className="text-white/60 text-sm md:text-base mt-3 max-w-xl [text-shadow:0_2px_20px_rgba(3,5,8,0.95)]">
                Supply, logistics and international connectivity — coordinated through trusted
                partners, from source to delivery.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
